import styled from '@emotion/styled';
import {useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import analytics from 'shared/utility/analytics';
import {PrimaryOutlineButton} from 'shared/buttons';
import {LabelText, Loader, LooptijdrenteMessagebar, NoRatesFound, Percentage, RadioCheck} from 'shared/components';
import {triggerScroll} from 'shared/helpers';
import {ConversieButtons} from 'shared/conversie';
import {RoundedRadioButton, SelectInput} from 'shared/inputs';
import {BaseRow, ResultCard} from 'shared/containers';
import {dispatchWithRequest, sync} from 'shared/wizard/actions';
import {requestWithValidation} from '../store';
import {getParamsFromState} from '../../../shared/helpers';
import {whiteListUrlParams} from '../config';
import SortingLabels from './overviewTable/SortingLabels';
import useMedia from 'shared/hooks/useMedia';
import {percentageFormatter} from '@dnhb/formatters';
import Info from 'shared/components/Info';

const LTV_OPTIONS = [
  {label: 'NHG', value: 'nhg'},
  {label: '70%', value: 70},
  {label: '80%', value: 80},
  {label: '90%', value: 90},
  { label: '100%', value: 100 },
];

const Row = styled(BaseRow)`
  display: flex;
  align-items: center;
  height: 72px;
  padding: 0 24px;
  margin-bottom: 8px;
  border: none;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.16);

  &:last-of-type {
    margin-bottom: 16px;
  }

  @media ${({ theme }) => theme.mobile} {
    flex-direction: column;
    border: 1px solid ${({ theme }) => theme.borderColor};
    max-height: 108px;
    height: 100%;
    padding: 24px 0 0;
    transition: max-height 200ms;
    overflow: hidden;

    ${props => openRowOnMobile(props)}
  }
`;

const openRowOnMobile = props => {
  if (props.openOnMobile) {
    return {
      maxHeight: '276px',
      borderColor: 'transparent',
      boxShadow: '0 0 16px rgba(0, 0, 0, 0.16);',
    };
  }
};

const ClickToLoanpartCells = styled.div`
  box-sizing: border-box;
  flex: 454px 5 0;
  display: flex;
  align-items: center;
  height: 100%;
  cursor: pointer;
  @media ${({ theme }) => theme.mobile} {
    width: 100%;
    flex: 64px 0 0;
    flex-wrap: wrap;
    align-items: flex-start;
    padding: 0 24px;
  }
`;

const ClickToActionCell = styled.div`
  flex: 152px 1 0;
  box-sizing: border-box;
  padding-left: 16px;
  button { height: 32px !important };
  
  @media ${({ theme }) => theme.mobile} {
    display: block;
    width: 100%;
    flex: auto 0 0;
    padding: 24px;
    
    button { height: 40px !important };
    > * + * { margin-top: 16px },
  };
`;

const MobileLabels = () => `
  flex: 50% 0 0;
  font-size: 14px;
  line-height: 16px;
  margin-top: 12px;
  text-overflow: ellipsis;
  overflow: hidden;  
  white-space: nowrap; 
`;

const Duration = styled('h2')`
  flex: 98px 1 0;

  @media ${({ theme }) => theme.mobile} {
    ${MobileLabels()};
    order: 4;
    text-align: right;
  }
`;

const Logo = styled.div`
  box-sizing: border-box;
  padding: 0 16px;
  flex: 124px 1 0;

  img {
    display: block;
    max-height: 40px;
    max-width: 92px;

    @supports (object-fit: contain) {
      width: 92px;
      height: 40px;
      object-fit: contain;
      object-position: center;
    }
  }

  @media ${({ theme }) => theme.mobile} {
    order: 1;
    flex-basis: unset;
    width: calc(50% - 32px);
    flex-grow: 0;

    img {
      max-width: 100%;
      max-height: 32px;

      @supports (object-fit: contain) {
        width: 100%;
        height: 32px;
        object-position: left;
      }
    }
  }
`;

const Label = styled.div`
  flex: 112px 2 0;

  @media ${({ theme }) => theme.mobile} {
    ${MobileLabels()};
    order: 3;
  }
`;

const Interest = styled.div`
  flex: 76px 1 0;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  padding-left: 8px;

  font-size: 24px;
  line-height: 32px;
  font-weight: 800;

  @media ${({ theme }) => theme.mobile} {
    order: 2;
    flex: 50% 0 0;
    justify-content: flex-end;
  }
`;

const TopFiveRow = ({ result }) => {
  const navigate = useNavigate();
  const {label_id, frt, frt_string, logo_url, mortgage_provider_name, interest_rate, label_name} = result;
  const dispatch = useDispatch();
  const syncAction = sync(dispatch);
  const { app, settings, framed, input, output } = useSelector(state => state);
  const isOpen = input.openTopRate === frt;
  const { onMobile } = useMedia();

  const navigateToOverview = () => {
    analytics.send('Navigeren', 'Overzicht');
    syncAction('label_id', label_id);
    syncAction('sorted_frt', frt * 12);
    navigate(`/${app}${framed ? '/framed' : ''}/all`);
    triggerScroll();
  };

  return (
    <Row openOnMobile={isOpen}>
      <ClickToLoanpartCells onClick={() => (onMobile ? syncAction('openTopRate', frt) : navigateToOverview())}>
        {onMobile && <RadioCheck active={isOpen} css={{ margin: '0 !important' }} />}
        <Duration>{frt_string}</Duration>
        <Logo>
          <img
              src={logo_url}
              title={mortgage_provider_name}
              alt={mortgage_provider_name}
          />
        </Logo>
        <Label title={label_name}>{label_name}</Label>
        {!onMobile && (
          <SortingLabels {...{ result, input }} isPreferred />
        )}
        <Interest>
          {onMobile ? (
            <Percentage value={interest_rate} fontSize="24" fontWeight="800" showDecimals />
          ) : (
            percentageFormatter(interest_rate)
          )}
        </Interest>
      </ClickToLoanpartCells>
      <ClickToActionCell>
        <ConversieButtons
          settings={settings}
          app={app}
          renderAs={onMobile ? 'PrimaryButton' : 'PrimaryOutlineButton'}
          css={{ '> button': { height: '40px' } }}
          onClick={() => syncAction('label_id', label_id)}
          urlLeadFormParams={getParamsFromState({ ...input, ...output, ...result }, whiteListUrlParams)}
        />
        {(onMobile || !settings.showLeadButton) && (
          <PrimaryOutlineButton onClick={navigateToOverview}>Toon in overzicht</PrimaryOutlineButton>
        )}
      </ClickToActionCell>
      {(onMobile && (result.has_auto_risk_class_reduction || result.collaboration)) && (
        <SortingLabels {...{ result, input }} isPreferred />
      )}
    </Row>
  );
};

const TopFive = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state);
  const navigate = useNavigate();
  const { app, framed, input = null, syncing, output = null } = state;
  const { onMobile } = useMedia();
  const noRates = useMemo(() => Object.keys(output?.topFive ?? []).length === 0, [output]);

  useEffect(() => {
    analytics.send('Openen', 'TopFive');
    requestWithValidation('calculate/interest-top-five', state, 'topFive');
  }, []);

  const onChange = (property, value) => {
    const request = '/renteoverzicht/calculate/interest-top-five';
    dispatch(dispatchWithRequest(property, value, request, 'topFive'));
  };

  const handleChange = value => {
    if (value !== 'nhg') onChange('ltv', parseInt(value));
    onChange('nhg', value === 'nhg');
  };

  const base = `/${app}${framed ? '/framed' : ''}`;

  return (
    <>
      <ResultCard>
        <form>
            <LabelText>Tariefklasse
              <Info title="Tariefklasse">
                De tariefklasse is gebaseerd op de verhouding tussen de hypotheek en de marktwaarde van de woning.
                Als je een woning hebt of gaat kopen met Nationale Hypotheek Garantie, val je in de tariefklasse NHG.
              </Info>
            </LabelText>
          {onMobile ? (
            <SelectInput
              placeholder="Tariefklasse"
              options={LTV_OPTIONS}
              isSearchable={false}
              value={input.nhg ? 'nhg' : input.ltv}
              onChange={({ value }) => handleChange(value)}
            />
          ) : (
            <div css={{ display: 'flex', '> label': { marginRight: '12px' } }}>
              {LTV_OPTIONS.map(option => (
                <RoundedRadioButton
                  key={option.label}
                  value={option.value}
                  name="ltv"
                  checked={input.nhg ? option.value === 'nhg' : option.value === input.ltv}
                  onChangeHandler={({ target: { value } }) => handleChange(value)}
                >
                  {option.label}
                </RoundedRadioButton>
              ))}
            </div>
          )}
        </form>
      </ResultCard>
      <LooptijdrenteMessagebar />
      <div css={{ paddingTop: '16px' }}>
        {syncing ? (
          <Loader />
        ) : (
          <>
            {noRates ? (
                <NoRatesFound message="Er zijn geen producten gevonden om weer te geven" />
            ) : (
                (output?.topFive ?? []).map(result => (
                    <TopFiveRow {...state} key={result.frt} result={result} />
                ))
            )}

            <PrimaryOutlineButton
              onClick={() => {
                analytics.send('Navigeren', 'Overzicht');
                navigate(`${base}/all`);
                triggerScroll();
              }}
              css={{ height: '56px' }}
            >
              Toon volledig renteoverzicht
            </PrimaryOutlineButton>
          </>
        )}
      </div>
    </>
  );
};

export default TopFive;
